import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BaseConnectorSelector from './BaseConnectorSelector';
import { connectorList } from '../services/connectorsConfig';
import { getConnectorPairCompatibility } from '../services/utilityService';

function ConnectorSelection({ 
  value = { brand: '', connector: '', gender: '', variant: '' }, 
  onChange, 
  onNext, 
  onBack, 
  end, 
  type, 
  otherConnector = null, 
  showCompatibility = false,
  onResetAll = null,
  onReady = null,
  onNavigationStateChange = null,
  onMissingRequirements = null
}) {

  const [isReady, setIsReady] = useState(false);
  const [navigationState, setNavigationState] = useState({
    isInFamilyMode: true,
    canGoBackToFamilies: false,
    handleBackToFamilies: null
  });

  // --- Compatibility Status List ---
  const [compatibleConnectors, setCompatibleConnectors] = useState([]);

  useEffect(() => {
    if (value && value.connector) {
      // Find all connectors that are compatible (including warnings)
      const compatible = connectorList.filter(conn => {
        if (conn.id === value.connector) return false;
        // Use the full connector ID from the connectorList for compatibility check
        const result = getConnectorPairCompatibility(value.connector, conn.id);
        // Include both 'compatible' and 'warning' status as both are technically compatible
        return result.status === 'compatible' || result.status === 'warning';
      }).map(conn => {
        // Get the compatibility result to include warning information
        const result = getConnectorPairCompatibility(value.connector, conn.id);
        return {
          ...conn,
          compatibilityResult: result
        };
      });
      setCompatibleConnectors(compatible.slice(0, 8)); // Limit to 8
    } else {
      setCompatibleConnectors([]);
    }
  }, [value]);

  // Check if we have a valid selection
  useEffect(() => {
    const missingRequirements = [];
    
    // --- End-to-end mode ---
    if (value && value.endA !== undefined && value.endB !== undefined) {
      const a = value.endA;
      const b = value.endB;
      const hasA = a && a.connector && a.gender;
      const hasB = b && b.connector && b.gender;
      
      if (!hasA) {
        missingRequirements.push('Please select a connector for End A');
      }
      if (!hasB) {
        missingRequirements.push('Please select a connector for End B');
      }
      
      const ready = !!(hasA && hasB);
      setIsReady(ready);
    } else {
      // --- Per-end mode ---
      const hasValidConnector = value && value.connector && value.connector.trim() !== '';
      const hasValidGender = value && value.gender && value.gender.trim() !== '';
      
      if (!hasValidConnector) {
        missingRequirements.push(`Please select a connector for End ${end}`);
      }
      if (!hasValidGender) {
        missingRequirements.push(`Please select a gender for End ${end}`);
      }
      
      // Check compatibility if we're showing compatibility and have both connectors
      let isCompatible = true;
      if (showCompatibility && otherConnector && hasValidConnector) {
        // Ensure otherConnector has the correct structure
        const otherConnectorId = otherConnector.connector || otherConnector.id || otherConnector;
        const compatibilityResult = getConnectorPairCompatibility(value.connector, otherConnectorId);
        // Allow proceeding with 'compatible' or 'warning' status (both are technically compatible)
        isCompatible = compatibilityResult.status === 'compatible' || compatibilityResult.status === 'warning';
        
        if (!isCompatible) {
          missingRequirements.push('Selected connectors are not compatible');
        }
      }
      
      const ready = hasValidConnector && hasValidGender && isCompatible;
      setIsReady(ready);
    }
    
    // Report missing requirements to parent
    if (onMissingRequirements) {
      onMissingRequirements(missingRequirements);
    }
  }, [value, end, showCompatibility, otherConnector, onMissingRequirements]);

  // Notify parent when readiness changes
  useEffect(() => {
    console.log('🔄 ConnectorSelection calling onReady with:', isReady, 'for end:', end, 'at timestamp:', Date.now());
    console.log('🔄 onReady function exists:', !!onReady, 'type:', typeof onReady);
    if (onReady) {
      console.log('🔄 About to call onReady...');
      onReady(isReady);
      console.log('🔄 Called onReady successfully');
    } else {
      console.log('🔄 onReady is null/undefined!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, end]);

  const handleConnectorChange = (connectorData) => {
    onChange(connectorData);
  };

  const handleNavigationStateChange = useCallback((newNavState) => {
    setNavigationState(newNavState);
    // Also notify parent component about navigation state changes
    if (onNavigationStateChange) {
      onNavigationStateChange(newNavState);
    }
  }, [onNavigationStateChange]);

  return (
    <Box sx={{ mt: 3 }}>
      {value && value.endA !== undefined && value.endB !== undefined ? (
        <></> // Do not render header/subheader in end-to-end mode
      ) : (
        <>
          <Typography variant="h5" gutterBottom>
            Select Connector for End {end}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Choose the connector that will be attached to End {end} of your cable.
            {showCompatibility && (
              <span> We'll show compatibility information for each connector option.</span>
            )}
          </Typography>
        </>
      )}
      


      {/* Show back to families button when in connector selection mode */}
      {!navigationState.isInFamilyMode && navigationState.canGoBackToFamilies && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={navigationState.handleBackToFamilies}
            sx={{ textTransform: 'none' }}
          >
            Back to connector types
          </Button>
        </Box>
      )}

      {/* Connector Selection */}
      <BaseConnectorSelector
        value={value}
        onChange={handleConnectorChange}
        type={type}
        end={end}
        showCompatibility={showCompatibility}
        otherConnector={otherConnector}
        onNavigationStateChange={handleNavigationStateChange}
      />

      {/* Compatibility Status List */}
      {value && value.connector && (
        <Box sx={{ mt: 3, p: 2, background: '#f6fafd', borderRadius: 2, border: '1px solid #e0e7ef' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Compatibility Status
          </Typography>
          {!otherConnector ? (
            <Typography variant="body2" color="text.secondary">
              Select the other connector to see compatibility information.
            </Typography>
          ) : compatibleConnectors.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Compatible Connectors (including warnings):
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {compatibleConnectors.map(conn => (
                  <li key={conn.id} style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>{conn.description || conn.label || conn.id}</span>
                    {conn.type ? <span style={{ color: '#888', marginLeft: 8 }}>({conn.type})</span> : null}
                    {conn.compatibilityResult && conn.compatibilityResult.status === 'warning' && (
                      <Tooltip 
                        title={conn.compatibilityResult.message || 'Compatible with warning'} 
                        arrow
                        placement="top"
                      >
                        <IconButton 
                          size="small" 
                          sx={{ 
                            ml: 1, 
                            p: 0.5,
                            color: '#ed6c02',
                            '&:hover': {
                              backgroundColor: 'rgba(237, 108, 2, 0.1)'
                            }
                          }}
                        >
                          <WarningAmberIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {conn.compatibilityResult && conn.compatibilityResult.status === 'compatible' && (
                      <Tooltip 
                        title="Fully compatible" 
                        arrow
                        placement="top"
                      >
                        <IconButton 
                          size="small" 
                          sx={{ 
                            ml: 1, 
                            p: 0.5,
                            color: '#2e7d32',
                            '&:hover': {
                              backgroundColor: 'rgba(46, 125, 50, 0.1)'
                            }
                          }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No compatible connectors found. Check individual connector cards for compatibility status.
            </Typography>
          )}
        </Box>
      )}


    </Box>
  );
}

export default ConnectorSelection; 